// @flow
import * as React from 'react';

import { productColor } from './designSystem.js';

const popoverBorderRadius = 8;

export function WistiaPopover({
  videoId,
  onClose,
}: {|
  videoId: string, // eg 'gk9w45pd5q'
  onClose: () => void,
|}) {
  // Our aspect ratios are all over the shop (278 distinct aspect ratios 😱)
  // The most frequent aspect ratio (at time of writing) is 4:3.
  // See here for some analysis:
  // https://paper.dropbox.com/doc/Wistia-Video-Aspect-Ratio-Determination--BhWNRojL805v05axiESTRSoMAg-T0kXoXIM3zvIuBEPoHrLv
  const aspectRatio = 4 / 3;
  const width = 716 + 2 * 16; // 748
  const height = width / aspectRatio; // 561

  const [hovered, setHovered] = React.useState(false);

  const [playbackWasInitiated, setPlaybackWasInitiated] = React.useState(false);
  const onFirstPlaybackInitiated = React.useCallback(() => {
    setPlaybackWasInitiated(true);
  }, []);

  const [isLoading, setIsLoading] = React.useState(true);

  // We show a loading state before the video is visually ready as otherwise
  // we get gross interstitial crap while Wistia is initializing. We enforce
  // a minimum loading state duration of 500ms to prevent quick flashes of
  // the loading state in cases where the load was too quick (loads are
  // ~100ms if you've already cached everything in memory — too slow
  // to avoid gross interstitial stuff, but too quick for hiding the
  // loading state immediately).
  const hasUnmounted = React.useRef(false);
  React.useEffect(() => () => ((hasUnmounted.current = true), undefined), []);
  const loadStartTime = React.useRef(Date.now()).current;
  const onReady = React.useCallback(() => {
    const minLoadingStateDuration = 500;
    const loadingDuration = Date.now() - loadStartTime;
    if (loadingDuration >= minLoadingStateDuration) {
      setIsLoading(false);
    } else {
      const timeout = minLoadingStateDuration - loadingDuration;
      setTimeout(() => {
        if (hasUnmounted.current) return;
        setIsLoading(false);
      }, timeout);
    }
  }, [loadStartTime]);

  // There is a compositing? bug in the Blink rendering engine (Chrome/Edge)
  // which produces aliasing artifacts on the rounded corners when compositing
  // a clipped media layer (the video) with a separate, non-transparent
  // background layer. If we have a video that perfectly fills the available
  // space, this bug leads to a pixelated black border on the rounded corners,
  // which looks awful. To avoid this bug, we only show the black background
  // if the video layout leads to a black fill around the video at-least equal
  // in size to the radius of the border. This ensures there is no
  // intersection between the media layer, and the clipping path.
  const [showBlackBackground, setShowBlackBackground] = React.useState(false);
  const onDeterminedVideoLayout = React.useCallback(
    (result) => {
      // If there is some corner case that causes us to not be able to determine
      // the layout, default to using the black background.
      if (result.outcome === 'failure') {
        setShowBlackBackground(true);
        return;
      }

      const { layout } = result;
      const isBlink = navigator.vendor === 'Google Inc.';
      if (isBlink) {
        // Only show the black background if there is enough room to
        // avoid the compositing bug manifesting.
        const showBlackBackground =
          layout.horizontalGap > popoverBorderRadius ||
          layout.verticalGap > popoverBorderRadius;
        setShowBlackBackground(showBlackBackground);
      } else {
        // The compositing bug doesn't exist for other render engines AFAICT,
        // so always show the black background for them.
        setShowBlackBackground(true);
      }
    },
    [setShowBlackBackground],
  );

  // There are two scenarios when we want to show our close overlay:
  //   1) When playback has never been initiated
  //   2) When hovering the video
  const showCloseOverlay = playbackWasInitiated === false || hovered;

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: 16,
          left: -16,
          boxShadow: '0px 8.75px 49px rgba(62, 62, 76, 0.4)',
          borderRadius: popoverBorderRadius,
          overflow: 'hidden',
          background: showBlackBackground ? '#000' : 'transparent',
          zIndex: 1,
        }}
      >
        <div
          style={{ position: 'relative' }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <WistiaEmbed
            videoId={videoId}
            width={width}
            height={height}
            onReady={onReady}
            onFirstPlaybackInitiated={onFirstPlaybackInitiated}
            onDeterminedVideoLayout={onDeterminedVideoLayout}
          />
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: productColor.cloudburst,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isLoading ? 1 : 0,
              pointerEvents: isLoading ? 'auto' : 'none',
              transition: 'opacity 300ms ease',
              color: productColor.white,
              fontWeight: 600,
            }}
          >
            Loading video...
          </div>
          <CloseOverlay isVisible={showCloseOverlay} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

function CloseOverlay({ isVisible, onClose }) {
  const [btnHovered, setBtnHovered] = React.useState(false);
  const transitionDuration = '200ms'; // Same as Wistia controls

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 100,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.5), transparent)',
          opacity: isVisible ? '1' : 0,
          transition: `opacity ${transitionDuration} ease`,
          // We want the pointer events to pass through to the underlaid
          // WistiaVideo as it has internal hover-tracking for displaying
          // it's controls that we don't want to interfere with.
          pointerEvents: 'none',
        }}
      />
      <button
        onMouseEnter={() => setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
        onClick={onClose}
        style={{
          background: `rgba(0, 0, 0, ${btnHovered ? 0.2 : 0})`,
          borderRadius: 4,
          border: 'none',
          padding: 2,
          position: 'absolute',
          top: 5,
          right: 5,
          opacity: isVisible ? 1 : 0,
          transition: `background opacity ${transitionDuration} ease`,
          pointerEvents: isVisible ? 'auto' : 'none',
        }}
      >
        <CloseIcon />
      </button>
    </>
  );
}

function CloseIcon() {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="#fff"
      style={{ display: 'block' }}
    >
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
    </svg>
  );
}

let uniqueId = 1;
function noop() {} // ensure stable reference

export function WistiaEmbed({
  videoId,
  width,
  height,
  onReady,
  onFirstPlaybackInitiated = noop,
  onDeterminedVideoLayout,
}: {|
  videoId: string,
  width: number,
  height: number,
  onReady: () => void,
  onFirstPlaybackInitiated?: () => void,
  onDeterminedVideoLayout: (
    {| outcome: 'success', layout: VideoLayout |} | {| outcome: 'failure' |},
  ) => void,
|}) {
  useLoadWistiaRuntime();
  useConfigureVideo({
    videoId,
    width,
    height,
    onReady,
    onFirstPlaybackInitiated,
    onDeterminedVideoLayout,
  });
  // We need a distinct key whenever we need to change the
  // videoConfig as Wistia's clumsy method of initialization
  // breaks things if the underlying DOM node is not fresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const key = React.useMemo(
    () => uniqueId++,
    [
      // 🚨 Dependency list must match the useEffect list in useConfigureVideo
      videoId,
      width,
      height,
      onReady,
      onFirstPlaybackInitiated,
      onDeterminedVideoLayout,
    ],
  );
  return (
    <div
      key={key}
      className={`wistia_embed wistia_async_${videoId}`}
      style={{ width, height }}
    />
  );
}

let hasScriptBeenInjected = false;
function useLoadWistiaRuntime() {
  React.useEffect(() => {
    if (hasScriptBeenInjected) return;
    hasScriptBeenInjected = true;
    const script = document.createElement('script');
    script.src = '//fast.wistia.com/assets/external/E-v1.js';
    document.head?.appendChild(script);
  }, []);
}

function useConfigureVideo({
  videoId,
  width,
  height,
  onReady,
  onFirstPlaybackInitiated,
  onDeterminedVideoLayout,
}: {|
  videoId: string,
  width: number,
  height: number,
  onReady: () => void,
  onFirstPlaybackInitiated: () => void,
  onDeterminedVideoLayout: (
    {| outcome: 'success', layout: VideoLayout |} | {| outcome: 'failure' |},
  ) => void,
|}) {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    let staleEffect = false;

    // wq stands for WistiaQueue. This is an idiotic mechanism by which
    // you can latch onto a video that exists (or will exist) in Wistia's
    // runtime. You push objects into this global array, which Wistia polls
    // for changes; removes objects it finds in there; and then modifies
    // the video model to change its configuration and register various
    // event handlers.
    window._wq = window._wq ?? [];
    const videoConfig = {
      id: videoId,
      // See https://wistia.com/support/developers/embed-options#options
      options: {
        playerColor: productColor.cloudburst,
        playButton: false,
        copyLinkAndThumbnailEnabled: false,
        // Do not show a black background in Wistia's DOM tree as we
        // want control over the background
        wmode: 'transparent',
        // TODO test this on iOS/iPadOS to see if the behaviour
        // difference is useful.
        // playsinline: false,
      },
      // NOTE: the "revoke" method won't actually revoke the config
      // for a video that already exists. It only means that new
      // videos added to the DOM won't have the config applied.
      // As such all these callbacks can/will fire after our effect
      // cleanup. Hence, we guard all callbacks (onHasData, onEmbedded
      // , onReady) with the staleEffect check.
      onHasData(video) {
        if (staleEffect) return;
        // Save a reference to the video as soon as its available.
        // onHasData is the first callback to fire that exposes it.
        videoRef.current = video;

        const uploadedVideo = video.data.media.assets.find(
          (asset) => asset.type === 'original',
        );
        if (uploadedVideo === undefined) {
          // Indicate a failure so we can fallback
          onDeterminedVideoLayout({ outcome: 'failure' });
        } else {
          onDeterminedVideoLayout({
            outcome: 'success',
            layout: computeVideoLayout({
              targetDimensions: { width, height },
              dimensions: {
                width: uploadedVideo.width,
                height: uploadedVideo.height,
              },
            }),
          });
        }
      },
      onEmbedded() {
        if (staleEffect) return;
      },
      onReady(video) {
        if (staleEffect) return;
        onReady();
        function onPlay() {
          onFirstPlaybackInitiated();
          video.unbind('play', onPlay);
        }
        video.bind('play', onPlay);
      },
    };
    window._wq.push(videoConfig);
    return () => {
      staleEffect = true;
      // We have to manually revoke any prior videoConfig that was passed
      // to Wistia's runtime 🙄. Without manually revoking these we would:
      //   a) be leaking memory
      //   b) end up with multiple event handlers registered for a given video
      //    for every time that video is displayed.
      window._wq.push({ revoke: videoConfig });
      if (videoRef.current !== null) {
        // Frees resources, stops data streaming etc
        videoRef.current.remove();
        videoRef.current = null;
      }
    };
  }, [
    // 🚨 Dependency list must match the useMemo list
    // used to generate the React element key.
    videoId,
    width,
    height,
    onReady,
    onFirstPlaybackInitiated,
    onDeterminedVideoLayout,
  ]);
}

// Describes the layout of the video within its container.
// The video is proportionally scaled to fit within the container
// and the transform origin is the center of the container.
type VideoLayout = {|
  width: number,
  height: number,
  // The size of the gap between the video and the top/bottom
  // edge of the container
  verticalGap: number,
  // The size of the gap between the video and the left/right
  // edge of the container
  horizontalGap: number,
|};

function computeVideoLayout({
  targetDimensions: td,
  dimensions: d,
}): VideoLayout {
  const targetAspectRatio = td.width / td.height;
  const aspectRatio = d.width / d.height;

  if (aspectRatio >= targetAspectRatio) {
    // too much width relative to height; width will fill available space
    const scale = td.width / d.width;
    const rescaledHeight = d.height * scale;
    return {
      width: td.width,
      height: rescaledHeight,
      verticalGap: (td.height - rescaledHeight) / 2,
      horizontalGap: 0,
    };
  } else {
    // too much height relative to width; height will fill available space
    const scale = td.height / d.height;
    const rescaledWidth = d.width * scale;
    return {
      width: rescaledWidth,
      height: td.height,
      verticalGap: 0,
      horizontalGap: (td.width - rescaledWidth) / 2,
    };
  }
}
